import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  Typography,
  Autocomplete,
  Chip,
  CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/Index";
import CustomTextField from "../../../components/ui/Textfield";
import apiService from "../../../service/Api";
import type { Stream } from "../types/crew-assignment";

// Debounce helper
function useDebounce(value: string, delay = 500) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debounced;
}

interface EventSelectorProps {
  selectedEvents: Stream[];
  onEventsChange: (events: Stream[]) => void;
}

const EventSelector: React.FC<EventSelectorProps> = ({
  selectedEvents,
  onEventsChange,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<Stream[]>([]);
  const [loading, setLoading] = useState(false);

  const debouncedSearch = useDebounce(inputValue, 400);

  const fetchEvents = useCallback(async (searchTerm: string) => {
    if (!searchTerm) {
      setOptions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.get<{ results: Stream[] }>(
        "/api/streams/stream/",
        { search: searchTerm, page_size: 10 }
      );
      setOptions(response.data.results || []);
    } catch (err) {
      console.error("Failed to fetch events:", err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents(debouncedSearch);
  }, [debouncedSearch, fetchEvents]);

  return (
    <Card sx={{ mb: 3, boxShadow: 0 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Select Events
        </Typography>
        <Autocomplete
          multiple
          options={options}
          getOptionLabel={(option) => {
            const deceasedName = option.booking.deceased?.[0]
              ? `${option.booking.deceased[0].first_name} ${option.booking.deceased[0].surname}`
              : "No Deceased";

            const bookingDate = option.service?.date_of_service
              ? new Date(option.service.date_of_service).toLocaleDateString()
              : "N/A";

            return `${deceasedName} - ${bookingDate}`;
          }}
          value={selectedEvents}
          onChange={(_, newValue) => onEventsChange(newValue)}
          inputValue={inputValue}
          onInputChange={(_, newInput) => setInputValue(newInput)}
          loading={loading}
          renderInput={(params) => (
            <CustomTextField
              {...params}
              placeholder="Search bookings..."
              variant="outlined"
              helperText="Type 3 letters to search"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const deceasedName = option.booking.deceased?.[0]
                ? `${option.booking.deceased[0].first_name} ${option.booking.deceased[0].surname}`
                : "No Deceased";

              const bookingDate = option.service?.date_of_service
                ? new Date(option.service.date_of_service).toLocaleDateString()
                : "N/A";

              return (
                <Chip
                  label={`${deceasedName} (${bookingDate})`}
                  {...getTagProps({ index })}
                  key={option.id}
                  style={{
                    backgroundColor: reduxTheme.primary_colors[800],
                    color: reduxTheme.primary_colors[100],
                  }}
                />
              );
            })
          }
          sx={{ minWidth: 300 }}
        />
      </CardContent>
    </Card>
  );
};

export default EventSelector;
